import LegalPage from "./LegalPage";

export default function AboutPage() {
  return (
    <LegalPage
      title="About"
      description="What the Cowork Cookbook is, how it was built, and who made it."
    >
      <p>
        The <strong>Cowork Cookbook</strong> is a public reference catalog of prompt-and-skill
        recipes for <strong>Microsoft 365 Copilot Cowork (Frontier)</strong>, with a focus on
        Dynamics 365 Finance &amp; Supply Chain Management. Every recipe is structured around the
        real plugin tool surface and the Microsoft Business Process Catalog so you can find a
        starting point for almost any process you work with.
      </p>

      <h2>What&apos;s in the catalog</h2>
      <ul>
        <li>
          <strong>1,377 recipes</strong> covering all 15 end-to-end domains and every L3 process
          node in the Business Process Catalog.
        </li>
        <li>
          <strong>29 recipes verified end-to-end</strong> against a live Cowork tenant with the
          USMF demo data — each one ships with a screenshot of the actual Cowork output and a
          tenant-data caveat documenting what the agent did, what data it found, and any honest
          limitations it surfaced.
        </li>
        <li>
          <strong>30 domain-scoped Cowork skills</strong> ready to drop into your OneDrive{" "}
          <code>Documents/Cowork/skills/</code> folder.
        </li>
        <li>
          <strong>Categories include</strong> Audit, Report, Dashboard, Scheduled brief, Configure,
          Demo data, Bulk update, PowerPoint deck, Teams update, and Adaptive Card — filter on the
          Search page.
        </li>
      </ul>

      <h2>How the catalog was created</h2>
      <ol>
        <li>
          <strong>Started from the real plugin surface, not guesses.</strong> The Dynamics 365 ERP
          plugin is a dynamic MCP server exposing 22 generic tools (data / form / action). Every
          recipe references the actual tool names (<code>data_find_entity_type</code>,{" "}
          <code>data_find_entities_sql</code>, etc.) — no fabricated action ids.
        </li>
        <li>
          <strong>Hand-curated the first 29.</strong> Drafted prompts grounded in the Microsoft
          Learn D365 F&amp;SCM documentation, then ran each one against a real Cowork tenant via
          Playwright. Captured the actual output (workbooks, 3D HTML, Adaptive Cards, email drafts)
          as screenshots and recorded what the agent actually did — including the cases where the
          tenant data wasn&apos;t there and the agent honestly stopped instead of fabricating.
        </li>
        <li>
          <strong>Generated the remaining 1,348 from a template generator.</strong> A
          Python generator (<code>scripts/generate_full_catalog.py</code>) walks all 674 L3 process
          nodes from the BPC taxonomy and emits two draft recipes per process from a rotation of 10
          templates — guaranteeing at least one read-only and one mutates-data recipe per process,
          plus creative variants (3D visualizations, scheduled briefs, PowerPoint decks, Teams
          posts, Adaptive Cards).
        </li>
        <li>
          <strong>Validated each verified recipe against USMF.</strong> 23 produced full
          end-to-end workbooks / visualizations. 6 returned constraint tables instead — those are
          documented as verified <em>with caveat</em>, because the agent&apos;s honest reasoning
          about what isn&apos;t reachable in D365 is itself a useful design artifact.
        </li>
        <li>
          <strong>Open source.</strong> Recipes live in the public{" "}
          <a href="https://github.com/seangalliher/Coworkcookbook">Coworkcookbook</a> repo (CC-BY-4.0).
          The web app is in <a href="https://github.com/seangalliher/CoworkCookBookWebApp">CoworkCookBookWebApp</a>.
        </li>
      </ol>

      <h2>Created by</h2>
      <p>
        <strong>Sean Galliher</strong> —{" "}
        <a
          href="https://www.linkedin.com/in/seanrgalliher/"
          target="_blank"
          rel="noopener noreferrer"
        >
          LinkedIn
        </a>
        . Issues, ideas, and pull requests welcome on the GitHub repos linked above.
      </p>

      <h2>Not affiliated with Microsoft</h2>
      <p>
        Microsoft 365 Copilot and Dynamics 365 are trademarks of Microsoft Corporation.
        This site is an independent community catalog and is not endorsed by or affiliated with
        Microsoft.
      </p>
    </LegalPage>
  );
}
